import { useMemo, useState } from "react";
import { BROWSABLE_CATEGORIES, CATEGORIES, ROUTES } from "@oshimap/config";
import { categoryColors } from "@oshimap/design-tokens";
import { normalizeCategoryCode } from "@oshimap/domain";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { navigate } from "../../app/router";
import { Button, CategoryChip, Chip, EmptyState, ErrorState, Skeleton, VerificationBadge, CategoryIcon } from "../../components/ui";
import { play } from "../../lib/motion";
import "./explore.css";
import { queryKeys } from "@oshimap/query";

/** FR-EXP-01: 지도 밖의 리스트형 Discovery 를 네비게이션에서 도달 가능하게 둔다. */
export function ExploreScreen({ category }: { category?: string }) {
  /*
   * 주소로 들어온 카테고리는 데이터가 아니라 **사용자가 요청한 것**이다.
   * `normalizeCategoryCode` 는 모르는 값을 DEFAULT 로 떨어뜨리는데 —
   * 데이터에는 맞는 처리지만 주소에는 아니다. 오타 난 링크로 들어온 사람에게
   * "일반 장소" 라는 있지도 않은 카테고리 화면을 보여주게 된다.
   */
  const code = category ? normalizeCategoryCode(category) : null;
  /* 격자에 없더라도 실재하는 분류면 보여준다 (예: CAFE 는 browsable 이 아닐 뿐 있는 분류다). */
  const known = category ? Object.hasOwn(CATEGORIES, category.toUpperCase()) : true;
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const { state, reload } = useQuery(
    queryKeys.places(code ? { category: code, pageSize: 50 } : { pageSize: 50 }),
    (signal) => ep.listPlaces(code ? { category: code, pageSize: 50 } : { pageSize: 50 }, { signal }),
  );

  const items = useMemo(() => {
    if (state.status !== "success") return [];
    return verifiedOnly
      ? state.data.filter((p) => p.verificationStatus === "VERIFIED" || p.verificationStatus === "OFFICIAL")
      : state.data;
  }, [state, verifiedOnly]);

  if (!code) {
    return (
      <div className="om-screen om-explore">
        <header className="om-explore__head">
          <h1 className="om-explore__title">탐색</h1>
          <button className="om-searchentry" onClick={() => navigate(ROUTES.search)}>
            <span aria-hidden="true">🔍</span> 어디로 덕질하러 갈까요?
          </button>
        </header>
        <h2 className="om-section__title">카테고리</h2>
        <div className="om-catgrid">
          {BROWSABLE_CATEGORIES.map((c) => {
            const col = categoryColors[c.code];
            return (
              <button key={c.code} className="om-cattile" style={{ borderColor: col.bg }}
                onClick={() => navigate(ROUTES.exploreCategory(c.code))}>
                <span className="om-cattile__ico"><CategoryIcon code={c.code} size={22} /></span>
                <b style={{ color: col.fg }}>{c.label}</b>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (!known) {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">탐색</h1>
        <EmptyState
          level={2}
          title="그런 카테고리가 없어요"
          body={`‘${category}’ 는 없는 분류입니다.`}
          action={<Button variant="primary" size="sm" onClick={() => navigate(ROUTES.explore)}>카테고리 보기</Button>}
        />
      </div>
    );
  }

  const def = CATEGORIES[code];
  return (
    <div className="om-screen om-explore">
      <header className="om-explore__head">
        <button className="om-back" onClick={() => navigate(ROUTES.explore)} aria-label="탐색으로">‹</button>
        <h1 className="om-explore__title">{def.label}</h1>
      </header>

      <div className="om-explore__filters" role="group" aria-label="필터">
        <Chip pressed={!verifiedOnly} onClick={(e) => { setVerifiedOnly(false); play(e.currentTarget, "mo-pop"); }}>전체</Chip>
        <Chip pressed={verifiedOnly} onClick={(e) => { setVerifiedOnly(true); play(e.currentTarget, "mo-pop"); }}>검증됨만</Chip>
      </div>

      {state.status === "loading" || state.status === "idle" ? (
        <div className="om-explore__body"><Skeleton lines={4} /></div>
      ) : state.status === "error" ? (
        <div className="om-explore__body"><ErrorState title="불러오지 못했어요" body={state.error.message} onRetry={reload} /></div>
      ) : items.length === 0 ? (
        <div className="om-explore__body">
          <EmptyState
            title={verifiedOnly ? "검증된 곳이 아직 없어요" : `${def.label}가 아직 없어요`}
            body={verifiedOnly ? "필터를 풀면 제보된 곳도 볼 수 있어요." : "알고 계신 곳을 제보해 주시면 지도에 올라갑니다."}
            action={verifiedOnly
              ? <Button variant="secondary" size="sm" onClick={() => setVerifiedOnly(false)}>필터 풀기</Button>
              : <Button variant="primary" size="sm" onClick={() => navigate(ROUTES.submit)}>제보하기</Button>}
          />
        </div>
      ) : (
        <div className="om-explore__body">
          <p className="om-count num">{items.length}곳</p>
          <div className="om-cards" style={{ padding: 0 }}>
            {items.map((p) => (
              <button key={p.id} className="om-card" onClick={() => navigate(ROUTES.place(p.id))}>
                <span className="om-card__thumb"><CategoryIcon code={p.category.code} size={26} /></span>
                <span className="om-card__body">
                  <span className="om-card__name">{p.name}</span>
                  <span className="om-card__row"><CategoryChip code={p.category.code} /></span>
                  <span className="om-card__row">
                    <VerificationBadge status={p.verificationStatus} lastVerifiedAt={p.lastVerifiedAt} />
                    {p.activeEventCount > 0 ? <span className="om-count num">진행 중 {p.activeEventCount}</span> : null}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
